import React, { FC, MutableRefObject, useEffect, useRef, useState } from 'react';
// @ts-ignore
import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
// @ts-ignore
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import baseAxios from 'axios';
import gs from 'query-string';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import API_URLS from '../../../constants/api-urls';
import { DETECTUM_SERVICES } from '../../../constants/detectum-config';
import Routes from '../../../constants/routes';
import UserIcon from '../../../images/usericon.png';
import translation, { world } from '../../../languages';
import { WORLD_GROUPS } from '../../../languages/constants';
import qualityEvents from '../../../services/quality-events';
import axios from '../../../utils/axios';
import cn from '../../../utils/classnames';
import detectumHelper from '../../../utils/detectum-helper';
import imagePathCreator from '../../../utils/image-path-creator';
import isAuthorDomain from '../../../utils/is-author-domain';
import formatPrice from '../../../utils/price/format-price';
import pubsub from '../../../utils/pubsub';
import makeUrl from '../../../utils/url/make-url';
import CurrencyPrice from '../../currency-price/currency-price';
import FontAwesomeIcon from '../../font-awesome-icon/font-awesome-icon';
import Autosuggest from '../../react-autosuggest/dist';
import { SEARCH_EVENT_TYPE } from '../search.constants';
import { EventConfigType, SearchConfig, applySearch, searchQualityEvents } from '../search.helpers';
import './auto-suggest-input.scss';

const getSuggestionValue = (suggestion: SuggestionType) => suggestion.query;

type AutoSuggestInputPropsType = TranslationProps &
    RouteComponentProps & {
        queryChange: (q: string) => void;
        mobileVisibleSearch: boolean;
        activeFilter: Filter;
        author: Author;
        isInteractive: boolean;
        authorsBottom: boolean;
        disableGeneralSuggestion: boolean;
        disableMaterialsSuggestion: boolean;
        isCopyPasted?: MutableRefObject<boolean>;
        searchText: string;
        onFocus: () => void;
        onBlur: () => void;
    };

const AutoSuggestInput: FC<AutoSuggestInputPropsType> = (props) => {
    const {
        queryChange,
        mobileVisibleSearch,
        history,
        searchText,
        activeFilter,
        author,
        disableMaterialsSuggestion,
        isInteractive,
        isCopyPasted,
        t
    } = props;
    const [suggestedMaterials, setSuggestedMaterials] = useState<SuggestedMaterialType[]>([]);
    const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
    const [value, setValue] = useState<string>((gs.parse(window.location.search).query as string) || '');
    const [highlightedSuggestion, setHighlightedSuggestion] = useState<boolean>(false);
    const [focused, setFocused] = useState<boolean>(false);
    // prevent firing onKeyDown after onSuggestionSelected callback (in case item is selected from the list pressing Enter)
    const blockSubmitHandler = useRef<boolean>(false);
    const funcRef = useRef<{ cancelDetectumRequest?: () => void; cancelLMPRequest?: () => void }>({
        cancelDetectumRequest: undefined,
        cancelLMPRequest: undefined
    });

    const useDetectumSuggest = !isAuthorDomain();

    useEffect(() => {
        const handleQuery = (e: Object, newValue: string) => {
            onChange(e, { newValue });
        };
        const token = pubsub.subscribe('query', handleQuery);

        return () => {
            pubsub.unsubscribe(token);
        };
    }, [value]);

    useEffect(() => {
        const listener = () => {
            if (focused && isCopyPasted) {
                isCopyPasted.current = true;
            }
        };

        window.addEventListener('paste', listener);

        return () => window.removeEventListener('paste', listener);
    }, [focused]);

    const onChange = (event: Object, { newValue }: { newValue: string }) => {
        if (queryChange) {
            queryChange(newValue);
        }

        if (isCopyPasted?.current && newValue.length <= 2) {
            isCopyPasted.current = false;
        }

        setValue(newValue);
    };

    const removeSuggestion = (e: React.MouseEvent<HTMLImageElement, MouseEvent>, suggestionText: string) => {
        e.preventDefault();
        e.stopPropagation();

        setSuggestions((prev) => prev.filter((s) => s.query !== suggestionText));
    };

    const renderSuggestion = (suggestion: SuggestionType, { query }: { query: string }) => {
        const suggestionText = suggestion.query;
        const matches = AutosuggestHighlightMatch(suggestionText, query);
        const parts: { highlight: string; text: string }[] = AutosuggestHighlightParse(suggestionText, matches);
        const author = suggestion.type === 'author';

        return (
            <div key={author ? suggestion.id : suggestionText} className='d-flex align-items-center'>
                {Boolean(author && suggestion.avatar_url) && (
                    <div
                        className='author-avatar float-left'
                        style={{
                            backgroundImage: `url(${imagePathCreator.resizedImg(
                                { url: suggestion.avatar_url },
                                { h: 25, q: 80 }
                            )})`
                        }}
                    />
                )}
                {Boolean(author && !suggestion.avatar_url) && (
                    <img alt='' className='author-avatar float-left' src={UserIcon} />
                )}
                <div
                    className={cn('d-flex align-items-center', {
                        'justify-content-between': !mobileVisibleSearch
                    })}
                >
                    {mobileVisibleSearch && Boolean(suggestion.isPastSearch) && (
                        <FontAwesomeIcon
                            onClick={(e) => removeSuggestion(e, suggestionText)}
                            className={cn('close-past mr-3')}
                            name='close'
                        />
                    )}
                    <div>
                        {parts.map((part) => (
                            <span className={cn({ highlight: part.highlight })}>{part.text}</span>
                        ))}
                    </div>
                    {!mobileVisibleSearch && Boolean(suggestion.isPastSearch) && (
                        <FontAwesomeIcon
                            onClick={(e) => removeSuggestion(e, suggestionText)}
                            className={cn('close-past')}
                            name='close'
                        />
                    )}
                </div>
            </div>
        );
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const onKeyDown = (e: React.ChangeEvent<HTMLInputElement> & { keyCode: number }) => {
        const suggestedMaterialSelected = suggestedMaterials.some((item) => item.active);

        if (blockSubmitHandler.current) {
            blockSubmitHandler.current = false;

            return;
        }

        if (e.keyCode === 39) {
            if (suggestedMaterials.length && highlightedSuggestion) {
                suggestedMaterials[0].active = true;
                e.preventDefault();
                e.stopPropagation();

                setSuggestedMaterials(suggestedMaterials);
            }
        } else if (e.keyCode === 40) {
            if (suggestedMaterialSelected) {
                let activeIndex = 0;

                suggestedMaterials.forEach((item, i) => {
                    if (item.active) {
                        activeIndex = i;
                        item.active = false;
                    }
                });

                if (activeIndex + 1 >= suggestedMaterials.length) {
                    suggestedMaterials[0].active = true;
                } else {
                    activeIndex++;
                    suggestedMaterials[activeIndex].active = true;
                }

                setSuggestedMaterials(suggestedMaterials);
            }
        } else if (e.keyCode === 37) {
            suggestedMaterials.forEach((item) => {
                item.active = false;
            });

            setSuggestedMaterials(suggestedMaterials);
            // eslint-disable-next-line sonarjs/no-collapsible-if
        } else if (e.keyCode === 38) {
            if (suggestedMaterialSelected) {
                let activeIndex = 0;

                suggestedMaterials.forEach((item, i) => {
                    if (item.active) {
                        activeIndex = i;
                        item.active = false;
                    }
                });

                if (activeIndex === 0) {
                    suggestedMaterials[suggestedMaterials.length - 1].active = true;
                } else {
                    activeIndex--;
                    suggestedMaterials[activeIndex].active = true;
                }

                setSuggestedMaterials(suggestedMaterials);
            }
        } else if (e.keyCode === 13) {
            const isAuthorPage = activeFilter.id === author.id;
            const eventConfig: EventConfigType = {
                query: e.target.value,
                isCopyPasted: isCopyPasted?.current
            };
            const config: SearchConfig = { author, isAuthorPage, isInteractive };

            resetMaterialSuggestion(true);
            onBlur();
            applySearch({
                history,
                config,
                eventConfig,
                data: {
                    query: e.target.value,
                    t: !isAuthorPage && activeFilter.id
                }
            });
        }
    };

    const resetMaterialSuggestion = (focused: boolean) => {
        setFocused(focused);
        setSuggestedMaterials([]);
    };

    const processPrice = (price: string) => formatPrice(price.split(' ')[0]);

    const onSuggestionHighlighted = (item: { suggestion: SuggestionType }) => {
        // prevent unnecessary rerenders and state changes
        if (highlightedSuggestion === Boolean(item.suggestion)) {
            return;
        }

        if (item.suggestion && item.suggestion.imgs) {
            setSuggestedMaterials(item.suggestion.imgs);
        }

        setHighlightedSuggestion(Boolean(item.suggestion));
    };

    const mouseOverMaterial = (item: SuggestedMaterialType) => {
        suggestedMaterials.forEach((material) => {
            material.active = material.name === item.name;
        });

        setSuggestedMaterials(suggestedMaterials);
    };

    const shouldRenderSuggestions = () => true;

    const fetchData = ({ value }: { value: string }) => {
        if (!value && props.disableGeneralSuggestion) {
            return;
        }
        if (funcRef?.current?.cancelDetectumRequest) {
            funcRef.current.cancelDetectumRequest();
        }
        if (funcRef?.current?.cancelLMPRequest) {
            funcRef?.current?.cancelLMPRequest();
        }

        if (useDetectumSuggest) {
            const url = detectumHelper.defineDetectumUrl(DETECTUM_SERVICES.AUTOSUGGEST);

            if (!url) {
                return;
            }

            const detectumPromise = baseAxios.get(url, formDetectumConfig(value));

            detectumPromise
                .then((response) => {
                    const processedDetectumResult = processDetectumResponse(response);

                    // const processedLMPResult = processLMPResponse(LMPResponse);
                    // const showSuggestedMaterialsInstantly = !processedLMPResult.suggestions.length;

                    setSuggestedMaterials(
                        !disableMaterialsSuggestion ? processedDetectumResult.suggestedMaterials : []
                    );
                    setSuggestions([...processedDetectumResult.suggestions]);
                })
                .catch(() => {});
        } else {
            axios.get(API_URLS.SEARCH, { params: { term: value } }).then((res) => {
                setSuggestions(processLMPResponse(res));
            });
        }
    };

    const formDetectumConfig = (value: string) => ({
        cancelToken: new baseAxios.CancelToken((c) => {
            funcRef.current.cancelDetectumRequest = c;
        }),
        params: {
            itemfields: 'slug,id',
            term: value
        },
        withCredentials: false
    });

    const formLMPConfig = (value: string) => ({
        cancelToken: new baseAxios.CancelToken((c) => {
            funcRef.current.cancelLMPRequest = c;
        }),
        params: {
            term: value
        }
    });

    const processDetectumResponse = (res: DetectumResType[]) => {
        const sortFunc = props.authorsBottom
            ? (a: DetectumResType, b: DetectumResType): number => Number(Boolean(a.author)) - Number(Boolean(b.author))
            : (a: DetectumResType, b: DetectumResType): number => Number(Boolean(b.author)) - Number(Boolean(a.author));

        const arr: SuggestionType[] = res.sort(sortFunc).map((r) => {
            let authorMixin = {};

            if (r.author) {
                const { id, slug, imagePath: avatar_url } = r.author;

                authorMixin = {
                    id,
                    slug,
                    avatar_url,
                    type: 'author'
                };
            }

            return {
                imgs: r.imgs,
                query: r.request,
                type: 'query',
                ...authorMixin
            };
        });

        return {
            suggestedMaterials: arr && arr[0] && arr[0].imgs ? arr[0].imgs : [],
            suggestions: arr
        };
    };

    const processLMPResponse = (res: SuggestionType[]) => {
        let suggestions = res;

        if (useDetectumSuggest) {
            suggestions = suggestions.filter((item) => item.type === 'author');
        }

        return suggestions.map((item) => ({ ...item, imgs: [] }));
    };

    const onSuggestionSelected = (
        _event: React.MouseEvent<HTMLImageElement>,
        { suggestion }: { suggestion: SuggestionType }
    ) => {
        const { query } = suggestion;
        const suggestedMaterialSelected = suggestedMaterials.some((item) => item.active);

        blockSubmitHandler.current = true;
        const eventConfig: EventConfigType = {
            query,
            isCopyPasted: isCopyPasted?.current,
            isSuggestion: true
        };

        if (WORLD_GROUPS.MAIN.includes(world)) {
            qualityEvents.track({
                extra: JSON.stringify({
                    value: query,
                    typedValue: value,
                    type: suggestion.type,
                    world
                }),
                type: SEARCH_EVENT_TYPE.SUGGESTION_SEARCH
            });
        }

        if (value.length >= 3 && parseFloat(value)) {
            eventConfig.type = SEARCH_EVENT_TYPE.MATERIAL_BY_ID_SEARCH;
            eventConfig.query = value;
        }

        if (suggestedMaterialSelected) {
            suggestedMaterials.forEach((item) => {
                if (item.active) {
                    selectSuggestedMaterial(item);
                }
            });
        } else if (suggestion.type === 'author') {
            const { id, slug } = suggestion;

            eventConfig.type = SEARCH_EVENT_TYPE.AUTHOR_SEARCH;

            setValue('');
            searchQualityEvents(eventConfig);
            history.push(makeUrl(Routes.AUTHOR_MAIN.path, { id, slug }));
        } else {
            const isAuthorPage = activeFilter.id === author.id;
            const config: SearchConfig = { author, isAuthorPage, isInteractive };

            applySearch({
                history,
                config,
                eventConfig,
                data: {
                    query: suggestion.query,
                    t: !isAuthorPage && activeFilter.id
                }
            });
        }
    };

    const selectSuggestedMaterial = (item: SuggestedMaterialType, e?: React.MouseEvent<HTMLInputElement>) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        history.push(
            makeUrl(Routes.MATERIAL.pathByWorld(item.world), { id: item.itemfields.id, slug: item.itemfields.slug }) +
                item.link
        );
        onBlur();
        resetMaterialSuggestion(true);
        onSuggestionsClearRequested();
    };

    const dropInputFocus = () => {
        setTimeout(() => {
            //@ts-ignore
            document.querySelector('.react-autosuggest__input')?.blur();
        }, 200);
    };

    const onSuggestionsClearRequested = () => {
        setSuggestions([]);
    };

    const setPlaceholder = () => {
        // if (window.location.pathname.indexOf('/autor') > -1) {
        //     return t('header.searchInput.placeholder.authorPage');
        // }

        if (typeof searchText !== 'undefined') {
            return searchText;
        }

        if (mobileVisibleSearch) {
            return t('header.searchInput.placeholder.mobile');
        }

        return t('header.searchInput.placeholder.desktop');
    };

    const onFocusInput = () => {
        resetMaterialSuggestion(true);
    };

    const onBlurInput = () => {
        dropInputFocus();
        resetMaterialSuggestion(false);
    };

    const suggestedMaterialSelected = suggestedMaterials.some((item) => item.active);
    const { onFocus = onFocusInput, onBlur = onBlurInput } = props;
    const inputProps = {
        onBlur,
        onChange,
        onFocus,
        onKeyDown,
        placeholder: setPlaceholder(),
        value
    };

    const renderSuggestionsContainer = ({
        containerProps,
        children
    }: {
        containerProps: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
        children: React.ReactNode;
    }) => {
        return (
            <div {...containerProps}>
                {children}
                {Boolean(suggestions.length && suggestedMaterials.length && focused && !disableMaterialsSuggestion) && (
                    <div className='suggested-materials'>
                        {!highlightedSuggestion && <span className='triangle' />}
                        {suggestedMaterials.map((item) => {
                            const price = processPrice(item.price);

                            return (
                                // eslint-disable-next-line
                                <div
                                    className={cn('suggested-material', { active: item.active })}
                                    key={item.itemfields.id}
                                    onClick={() => selectSuggestedMaterial(item)}
                                    onMouseOver={() => mouseOverMaterial(item)}
                                >
                                    <div
                                        className='preview'
                                        style={{
                                            backgroundImage: `url(${imagePathCreator.optimizedImg(
                                                { preview: item.image_url },
                                                { h: 100 }
                                            )})`
                                        }}
                                    />
                                    <div className='title'>
                                        <b>{item.name}</b>
                                        <div className='mt-1'>
                                            {price === '0,00' ? (
                                                t('common:common.materialBlock.free')
                                            ) : (
                                                <CurrencyPrice price={price} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Autosuggest
            blockArrows={suggestedMaterialSelected}
            focusInputOnSuggestionClick={false}
            getSuggestionValue={getSuggestionValue}
            inputProps={inputProps}
            renderSuggestion={renderSuggestion}
            renderSuggestionsContainer={renderSuggestionsContainer}
            shouldRenderSuggestions={shouldRenderSuggestions}
            suggestions={suggestions}
            onSuggestionHighlighted={onSuggestionHighlighted}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionsFetchRequested={fetchData}
        />
    );
};

const translate = translation(['header', 'common']);

export default withRouter(translate(AutoSuggestInput));
